import type { ReactNode } from 'react'

import { cn } from 'lib/utils'

import { ErrorBlock, LoadingBlock, RestrictedBlock, UnavailableBlock, UnknownBlock } from '../blocks/BlockStatus'
import { rendererFor } from '../blocks/registry'
import type { BlockView } from '../hooks/useResolvedBlocks'
import { LAYOUT_SPECS, STAGE_HEIGHT, STAGE_WIDTH, regionsOf, themeOf } from '../layouts'
import type { Block, Slide } from '../types/presentation.types'

/**
 * One slide, drawn on a 1280x720 logical stage (MYD-495).
 *
 * ## PURE, and that is load-bearing
 *
 * `(slide, resolved blocks, theme) -> JSX`. No store, no fetching, no effects.
 * The editor canvas, the sorter thumbnails, the presenter (MYD-496), the phone
 * viewer and the print route (Phase 5) all draw slides through this one
 * component, so a slide looks the same everywhere and PDF export needs no
 * second set of templates. Anything stateful belongs to the caller.
 *
 * `interaction` is the one concession to the editor: optional callbacks for
 * selecting a block and filling an empty region. It is plain props, so the
 * component stays pure; the presenter simply leaves it out.
 *
 * Render at stage size and scale with `SlideStage`. Never restyle the inside
 * per viewport: the stage is what makes the thumbnail and the projector match.
 */

export interface SlideInteraction {
	selectedBlockId?: string | null
	onSelectBlock?: (blockId: string) => void
	/** Rendered in a region with no blocks, e.g. an "Add element" button. */
	renderEmptyRegion?: (region: string) => ReactNode
	/** Rendered after a region's blocks, e.g. a small "+" to stack another. */
	renderRegionFooter?: (region: string) => ReactNode
}

export interface SlideRendererProps {
	slide: Slide
	/** Resolved content by block id. A missing entry renders as loading. */
	blocks: Record<string, BlockView | undefined>
	theme?: string
	interaction?: SlideInteraction
	className?: string
}

/** One block's content, or the placeholder for its status. */
export function BlockContent({ block, view }: { block: Block; view: BlockView | undefined }) {
	const renderer = rendererFor(block.element)
	const fill = Boolean(renderer?.fill)

	if (!view || view.status === 'loading') return <LoadingBlock fill={fill} />
	if (view.status === 'restricted') return <RestrictedBlock fill={fill} />
	if (view.status === 'unavailable') return <UnavailableBlock fill={fill} />
	if (view.status === 'error') return <ErrorBlock fill={fill} message={view.message} />
	if (!renderer) return <UnknownBlock fill={fill} />

	// A published snapshot's map or chart (MYD-508): the image captured at
	// publish time, never a live map or a redrawn chart.
	if (view.status === 'ok' && view.rasterUrl) {
		return <img src={view.rasterUrl} alt="" className={cn('w-full object-contain', fill ? 'h-full min-h-0' : 'h-auto')} draggable={false} data-testid="raster-block" />
	}

	const Component = renderer.component
	return <Component payload={view.payload} />
}

export function SlideRenderer({ slide, blocks, theme, interaction, className }: SlideRendererProps) {
	const spec = LAYOUT_SPECS[slide.layout] ?? LAYOUT_SPECS['title-body']
	const colors = themeOf(theme)
	const regions = regionsOf(slide.layout)

	const blocksIn = (region: string) => slide.blocks.filter(block => block.region === region)

	const renderRegion = (region: string) => {
		const inRegion = blocksIn(region)
		return (
			<div
				key={region}
				data-region={region}
				className={cn('relative flex min-h-0 flex-col gap-6 overflow-hidden', spec.regions[region])}
			>
				{inRegion.length === 0
					? interaction?.renderEmptyRegion?.(region) ?? null
					: inRegion.map(block => {
							const fill = Boolean(rendererFor(block.element)?.fill)
							const selectable = Boolean(interaction?.onSelectBlock)
							const selected = interaction?.selectedBlockId === block.id
							return (
								<div
									key={block.id}
									data-block-id={block.id}
									// Read by the PowerPoint export (MYD-512) to choose native shapes or a picture.
									data-element={block.element}
									className={cn(
										'relative flex min-h-0 flex-col',
										fill && 'flex-1',
										// An INSET ring: regions clip overflow, so anything drawn
										// outside the block (an outline with an offset) is invisible.
										selectable && 'cursor-pointer rounded-lg hover:ring-2 hover:ring-inset hover:ring-primary/40',
										selected && 'ring-4 ring-inset ring-primary hover:ring-4 hover:ring-primary',
									)}
									onClick={
										selectable
											? event => {
													event.stopPropagation()
													interaction?.onSelectBlock?.(block.id)
												}
											: undefined
									}
								>
									<BlockContent block={block} view={blocks[block.id]} />
								</div>
							)
						})}
				{inRegion.length > 0 && interaction?.renderRegionFooter?.(region)}
			</div>
		)
	}

	return (
		<div
			className={cn('relative overflow-hidden', colors.stage, className)}
			style={{ width: STAGE_WIDTH, height: STAGE_HEIGHT }}
			data-layout={slide.layout}
		>
			{spec.frame === 'centered' ? (
				<>
					{slide.layout === 'title' && (
						// The background region sits behind the title, dimmed only when it
						// holds something AND there is title text to read over it. An
						// empty cover stays white, and a block that brings its own text
						// (location.cover, MYD-499) is not washed out for nothing.
						<div className={cn(spec.grid, 'flex')}>
							{regions.map(renderRegion)}
							{blocksIn('background').length > 0 && (slide.title || slide.subtitle) && (
								<div className="pointer-events-none absolute inset-0 bg-white/70" data-testid="title-dim" />
							)}
						</div>
					)}
					<div className="pointer-events-none relative flex h-full flex-col items-center justify-center px-24 text-center">
						{slide.title && <h1 className={cn('text-[72px] font-bold leading-tight', colors.title)}>{slide.title}</h1>}
						{slide.subtitle && <p className={cn('mt-6 text-[32px]', colors.subtitle)}>{slide.subtitle}</p>}
						{slide.layout === 'section' && <div className="mt-10 h-1.5 w-24 rounded-full bg-primary" />}
					</div>
				</>
			) : (
				<div className="flex h-full flex-col px-16 pb-14 pt-12">
					{(slide.title || slide.subtitle) && (
						<header className="mb-8 shrink-0">
							{slide.title && <h1 className={cn('text-[44px] font-bold leading-tight', colors.title)}>{slide.title}</h1>}
							{slide.subtitle && <p className={cn('mt-2 text-[24px]', colors.subtitle)}>{slide.subtitle}</p>}
						</header>
					)}
					<div className={cn('min-h-0 flex-1', spec.grid)}>{regions.map(renderRegion)}</div>
				</div>
			)}
		</div>
	)
}

export default SlideRenderer
